import type { IRequiredActionIssue } from '@/composables/requiredAction/requiredAction.types';
import { SUPPORT_MEASURE_PROTOCOL_TEXT_MAX_LENGTH } from 'common/constants/support.measure.constants';

export const SupportMeasureProtocolRequiredAction = {
  CompetenceRating: 'competenceRating',
  Students: 'students',
  Text: 'text',
} as const;

export type TSupportMeasureProtocolRequiredAction =
  (typeof SupportMeasureProtocolRequiredAction)[keyof typeof SupportMeasureProtocolRequiredAction];

export interface ISupportMeasureProtocolValidationIssue
  extends IRequiredActionIssue<TSupportMeasureProtocolRequiredAction> {
  competenceId?: number;
}

export interface ISupportMeasureProtocolValidationContext {
  competenceIds: readonly number[];
  levelByCompetenceId: Readonly<Record<number, number | null>>;
  resolvedStudentCount: number;
  text: string;
}

export const getSupportMeasureProtocolValidationIssues = (
  context: ISupportMeasureProtocolValidationContext,
): ISupportMeasureProtocolValidationIssue[] => {
  const issues: ISupportMeasureProtocolValidationIssue[] = [];
  const textLength = context.text.trim().length;

  if (textLength === 0) {
    issues.push({
      action: SupportMeasureProtocolRequiredAction.Text,
      code: 'text.required',
      messageKey: 'supportMeasureEntry.protocolRequiredAction.text',
    });
  } else if (textLength > SUPPORT_MEASURE_PROTOCOL_TEXT_MAX_LENGTH) {
    issues.push({
      action: SupportMeasureProtocolRequiredAction.Text,
      code: 'text.tooLong',
      messageKey: 'supportMeasureEntry.protocolRequiredAction.textTooLong',
    });
  }

  if (context.resolvedStudentCount === 0) {
    issues.push({
      action: SupportMeasureProtocolRequiredAction.Students,
      code: 'students.required',
      messageKey: 'supportMeasureEntry.protocolRequiredAction.students',
    });
  }

  for (const competenceId of context.competenceIds) {
    if (context.levelByCompetenceId[competenceId] != null) continue;
    issues.push({
      action: SupportMeasureProtocolRequiredAction.CompetenceRating,
      code: `competenceRating.required.${competenceId}`,
      competenceId,
      messageKey: 'supportMeasureEntry.protocolRequiredAction.competenceRating',
    });
  }

  return issues;
};
